// src/firebase/customer-profile-functions.js

import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, DEFAULT_STORE_ID } from "./firebase";

/**
 * Fetch customer profile from the active store's customers subcollection
 * @param {string} userId - User ID
 * @param {string} storeId - Store ID (defaults to DEFAULT_STORE_ID)
 * @returns {Promise<object|null>} Customer profile data or null if not found
 */
export const fetchStoreCustomerProfile = async (userId, storeId) => {
    try {
        if (!userId || !storeId) {
            throw new Error("User ID and Store ID are required");
        }

        const customerRef = doc(db, 'stores', storeId, 'customers', userId);
        const customerDoc = await getDoc(customerRef);

        if (customerDoc.exists()) {
            const data = customerDoc.data();

            // Ensure the data structure matches the order format
            return {
                customerInfo: data.customerInfo || {},
                billingInfo: data.billingInfo || {},
                shippingInfo: data.shippingInfo || {},
                createdAt: data.createdAt,
                updatedAt: data.updatedAt
            };
        } else {
            return null;
        }
    } catch (error) {
        console.error("Error fetching store customer profile:", error);
        throw error;
    }
};

/**
 * Create or update customer profile in the store's customers subcollection
 * @param {string} userId - User ID
 * @param {object} profileData - Customer profile data
 * @param {string} storeId - Store ID (defaults to DEFAULT_STORE_ID)
 * @returns {Promise<object>} Updated customer profile
 */
export const updateStoreCustomerProfile = async (userId, storeId, profileData) => {
    try {
        if (!userId || !storeId) {
            throw new Error("User ID and Store ID are required");
        }

        const customerRef = doc(db, 'stores', storeId, 'customers', userId);

        // Structure the data to match order format
        const updateData = {
            customerInfo: {
                firstName: profileData.firstName || '',
                lastName: profileData.lastName || '',
                email: profileData.email || '',
                phone: profileData.phone || '',
                address: profileData.address || '',
                city: profileData.city || '',
                state: profileData.state || '',
                zipCode: profileData.zipCode || '',
                country: profileData.country || ''
            },
            billingInfo: {
                firstName: profileData.firstName || '',
                lastName: profileData.lastName || '',
                email: profileData.email || '',
                phone: profileData.phone || '',
                address: profileData.address || '',
                city: profileData.city || '',
                state: profileData.state || '',
                zipCode: profileData.zipCode || '',
                country: profileData.country || ''
            },
            shippingInfo: {
                firstName: profileData.firstName || '',
                lastName: profileData.lastName || '',
                email: profileData.email || '',
                phone: profileData.phone || '',
                address: profileData.address || '',
                city: profileData.city || '',
                state: profileData.state || '',
                zipCode: profileData.zipCode || '',
                country: profileData.country || ''
            },
            updatedAt: serverTimestamp()
        };

        await setDoc(customerRef, updateData, { merge: true });

        return true;
    } catch (error) {
        console.error("Error updating store customer profile:", error);
        throw error;
    }
};

/**
 * Create customer profile from checkout data
 * @param {string} userId - User ID  
 * @param {object} checkoutData - Checkout form data
 * @param {string} storeId - Store ID (defaults to DEFAULT_STORE_ID)
 * @returns {Promise<object>} Created customer profile
 */
export const createCustomerProfileFromCheckout = async (userId, checkoutData, storeId = DEFAULT_STORE_ID) => {
    const profileData = {
        // Personal information
        displayName: `${checkoutData.billingInfo.firstName} ${checkoutData.billingInfo.lastName}`,
        firstName: checkoutData.billingInfo.firstName,
        lastName: checkoutData.billingInfo.lastName,
        email: checkoutData.billingInfo.email,
        phoneNumber: checkoutData.billingInfo.phone,

        // Billing address
        billingAddress: {
            address: checkoutData.billingInfo.address,
            city: checkoutData.billingInfo.city,
            state: checkoutData.billingInfo.state,
            zipCode: checkoutData.billingInfo.zipCode,
            country: checkoutData.billingInfo.country
        },

        // Shipping address (if different from billing)
        shippingAddress: checkoutData.sameAsBilling ? null : {
            address: checkoutData.shippingInfo.address,
            city: checkoutData.shippingInfo.city,
            state: checkoutData.shippingInfo.state,
            zipCode: checkoutData.shippingInfo.zipCode,
            country: checkoutData.shippingInfo.country
        },

        // Preferences
        sameAsBilling: checkoutData.sameAsBilling || true,

        // Metadata
        storeId: storeId,
        lastOrderAt: serverTimestamp()
    };

    return await updateStoreCustomerProfile(userId, profileData, storeId);
};

/**
 * Get formatted address for display
 * @param {object} address - Address object
 * @returns {string} Formatted address string
 */
export const formatAddress = (address) => {
    if (!address) return '';

    const parts = [
        address.address,
        address.city,
        address.state,
        address.zipCode,
        address.country
    ].filter(Boolean);

    return parts.join(', ');
};

/**
 * Validate customer profile data
 * @param {object} profileData - Profile data to validate
 * @returns {object} Validation result with isValid flag and errors array
 */
export const validateCustomerProfile = (profileData) => {
    const errors = [];

    // Required fields
    if (!profileData.firstName?.trim()) {
        errors.push('First name is required');
    }

    if (!profileData.lastName?.trim()) {
        errors.push('Last name is required');
    }

    if (!profileData.email?.trim()) {
        errors.push('Email is required');
    } else if (!/\S+@\S+\.\S+/.test(profileData.email)) {
        errors.push('Email format is invalid');
    }

    if (!profileData.phoneNumber?.trim()) {
        errors.push('Phone number is required');
    }

    // Billing address validation
    if (profileData.billingAddress) {
        if (!profileData.billingAddress.address?.trim()) {
            errors.push('Billing address is required');
        }
        if (!profileData.billingAddress.city?.trim()) {
            errors.push('Billing city is required');
        }
        if (!profileData.billingAddress.state?.trim()) {
            errors.push('Billing state is required');
        }
        if (!profileData.billingAddress.country?.trim()) {
            errors.push('Billing country is required');
        }
    }

    // Shipping address validation (if different from billing)
    if (!profileData.sameAsBilling && profileData.shippingAddress) {
        if (!profileData.shippingAddress.address?.trim()) {
            errors.push('Shipping address is required');
        }
        if (!profileData.shippingAddress.city?.trim()) {
            errors.push('Shipping city is required');
        }
        if (!profileData.shippingAddress.state?.trim()) {
            errors.push('Shipping state is required');
        }
        if (!profileData.shippingAddress.country?.trim()) {
            errors.push('Shipping country is required');
        }
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};